import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Link as ScrollLink } from 'react-scroll';
import { event as gaEvent } from '../../utils/analytics';

interface DropdownItem {
  label: string;
  path: string;
}

interface NavItem {
  name: string;
  label: string;
  path: string;
  dropdown?: DropdownItem[];
}

const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [activeSection, setActiveSection] = useState('home');
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const dropdownRefs = useRef<{ [key: string]: HTMLLIElement | null }>({});

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (activeDropdown && 
          dropdownRefs.current[activeDropdown] && 
          !dropdownRefs.current[activeDropdown]?.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdown]);

  const navItems: NavItem[] = [
    { name: 'about', label: 'About Us', path: '/about' },
    { name: 'services', label: 'Services', path: '/services' },
    { name: 'universities', label: 'Universities', path: '/universities' },
    { name: 'gallery', label: 'Gallery', path: '/gallery' },
    { name: 'contact', label: 'Contact', path: '/contact' }
  ];

  const handleDropdownToggle = (name: string) => {
    setActiveDropdown(activeDropdown === name ? null : name);
  };

  const handleApplyClick = () => {
    // Track click on Apply Now button in navbar
    gaEvent({
      action: 'apply_button_click',
      category: 'Navigation',
      label: 'Navbar Apply Now'
    });
  };

  return (
    <motion.nav
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? 'glass-dark py-3 shadow-lg shadow-black/5'
          : 'glass-dark bg-black/50 backdrop-blur-md py-6'
      }`}
    >
      <div className="container mx-auto px-4 flex justify-between items-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex items-center"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <RouterLink to="/" className="relative">
            <h1 className="text-2xl font-bold">
              {/* <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">Study in Japan</span> */}
              <img src="../../../recall.svg" alt="" className='h-16' />
            </h1>
            <motion.div
              className="absolute -bottom-1 left-0 h-0.5 bg-gradient-to-r from-primary to-secondary"       
              initial={{ width: 0 }}
              animate={{ width: '100%' }}
              transition={{ delay: 0.5, duration: 0.8 }}
            />
          </RouterLink>
        </motion.div>

        {/* Desktop Navigation */}
        <motion.ul
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="hidden md:flex space-x-8"
        >
          {navItems.map((item) => (
            <motion.li 
              key={item.name}
              whileHover={{ y: -2 }}
              whileTap={{ y: 0 }}
              className="relative"
              ref={(el) => {
                if (item.dropdown) {
                  dropdownRefs.current[item.name] = el;
                }
              }}
            >
              {item.dropdown ? (
                <>
                  <button
                    onClick={() => handleDropdownToggle(item.name)}
                    className="relative text-slate-100 hover:text-primary transition-colors duration-200 cursor-pointer group font-medium flex items-center"
                  >
                    <div className="flex items-center">
                      <span className="mr-2 text-primary">→</span>
                      <span 
                        className="text-lg font-bold text-black group-hover:text-primary transition-all duration-300"
                        style={{ textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)' }}
                      >{item.label}</span>
                    </div>
                    <svg 
                      className={`w-4 h-4 ml-1 transition-transform duration-200 ${activeDropdown === item.name ? 'rotate-180' : ''}`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                    <motion.span
                      className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"
                      animate={{
                        width: activeSection === item.name || activeDropdown === item.name ? '100%' : '0%'
                      }}
                    />
                  </button>
                  <AnimatePresence>
                    {activeDropdown === item.name && (
                      <motion.div
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -5 }}
                        transition={{ duration: 0.2 }}
                        className="absolute mt-2 w-64 glass-dark border border-white/10 rounded-lg shadow-lg z-50 py-2"
                      >
                        {item.dropdown.map((dropdownItem) => (
                          <RouterLink
                            key={dropdownItem.path}
                            to={dropdownItem.path}
                            className="block px-4 py-2 text-sm text-slate-100 hover:bg-white/10 hover:text-primary transition-colors duration-200"
                            onClick={() => setActiveDropdown(null)}
                          >
                            {dropdownItem.label}
                          </RouterLink>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </>
              ) : (
                <RouterLink
                  to={item.path}
                  className="relative text-slate-100 hover:text-primary transition-colors duration-200 cursor-pointer group font-medium text-lg tracking-wide"
                >
                  <span 
                    className="text-black font-bold group-hover:text-primary transition-all duration-300"
                    style={{ textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)' }}
                  >{item.label}</span>
                  <motion.span
                    className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"
                    animate={{
                      width: activeSection === item.name ? '100%' : '0%'
                    }}
                  />
                </RouterLink>
              )}
            </motion.li>
          ))}
        </motion.ul>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="hidden md:block"
        >
          <RouterLink to="/apply" onClick={handleApplyClick}>
            <motion.button
              whileHover={{
                scale: 1.05,
                boxShadow: "0px 0px 8px rgba(227, 6, 19, 0.6)"
              }}
              whileTap={{ scale: 0.95 }}
              className="px-6 py-2 rounded-lg bg-gradient-to-r from-primary to-secondary text-white font-semibold transition-all"
            >
              Apply Now
            </motion.button>
          </RouterLink>
        </motion.div>

        {/* Mobile menu button */}
        <motion.div
          className="md:hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <motion.button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-white focus:outline-none relative"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <div className="w-8 h-8 flex items-center justify-center">
              <AnimatePresence mode="wait">
                {isMobileMenuOpen ? (
                  <motion.svg
                    key="close"
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    initial={{ opacity: 0, rotate: -90 }}
                    animate={{ opacity: 1, rotate: 0 }}
                    exit={{ opacity: 0, rotate: 90 }}
                    transition={{ duration: 0.3 }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </motion.svg>
                ) : (
                  <motion.svg
                    key="menu"
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    initial={{ opacity: 0, rotate: 90 }}
                    animate={{ opacity: 1, rotate: 0 }}
                    exit={{ opacity: 0, rotate: -90 }}
                    transition={{ duration: 0.3 }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </motion.svg>
                )}
              </AnimatePresence>
            </div>
          </motion.button>
        </motion.div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden glass-dark backdrop-blur-lg border-t border-white/10"
          >
            <ul className="px-4 py-4 max-h-[80vh] overflow-y-auto">
              {navItems.map((item, index) => (
                <motion.li
                  key={item.name}
                  className="py-3 border-b border-white/5 last:border-0"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index }}
                >
                  {item.dropdown ? (
                    <div>
                      <button
                        onClick={() => handleDropdownToggle(item.name)}
                        className="text-slate-100 hover:text-primary transition-colors duration-200 cursor-pointer block w-full text-left"
                      >
                        <motion.div
                          whileHover={{ x: 5 }}
                          transition={{ type: "spring", stiffness: 400, damping: 10 }}
                          className="flex items-center justify-between group"
                        >
                          <div className="flex items-center">
                            <span className="mr-2 text-primary">→</span>
                            <span 
                              className="text-lg font-bold text-black group-hover:text-primary transition-all duration-300"
                              style={{ textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)' }}
                            >{item.label}</span>
                          </div>
                          <svg 
                            className={`w-4 h-4 transition-transform duration-200 ${activeDropdown === item.name ? 'rotate-180' : ''}`} 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </motion.div>
                      </button>
                      <AnimatePresence>
                        {activeDropdown === item.name && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="ml-4 mt-2 space-y-1"
                          >
                            {item.dropdown.map((dropdownItem) => (
                              <RouterLink
                                key={dropdownItem.path}
                                to={dropdownItem.path}
                                className="block py-2 pl-6 border-l border-white/10 text-slate-100 hover:text-primary text-sm transition-colors duration-200"
                                onClick={() => setIsMobileMenuOpen(false)}
                              >
                                {dropdownItem.label}
                              </RouterLink>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <RouterLink
                      to={item.path}
                      className="text-slate-100 hover:text-primary transition-colors duration-200 cursor-pointer block"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <motion.div
                        whileHover={{ x: 5 }}
                        transition={{ type: "spring", stiffness: 400, damping: 10 }}
                        className="flex items-center"
                      >
                        <span className="mr-2 text-primary">→</span>
                        <span 
                          className="text-lg font-bold text-black"
                          style={{ textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)' }}
                        >{item.label}</span>
                      </motion.div>
                    </RouterLink>
                  )}
                </motion.li>
              ))}
              <motion.li
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * navItems.length }}
                className="pt-4 pb-2"
              >
                <RouterLink to="/apply" onClick={() => {
                  setIsMobileMenuOpen(false);
                  handleApplyClick();
                }}>
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    className="w-full px-6 py-3 rounded-lg bg-gradient-to-r from-primary to-secondary text-white font-semibold transition-all"
                  >
                    Apply Now
                  </motion.button>
                </RouterLink>
              </motion.li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar; 